/**
 * Custom Form 작성 답변(L_CUSTOM_FORM_ANSWER) → Singbac Incorporation (Pre) 문서 값.
 *
 * 커스텀 폼은 클라이언트가 자유롭게 만들어 질문 id/라벨이 폼마다 임의다. 따라서
 * **라벨 유사매칭**으로 폼 질문을 Singbac 3종 문서 변수에 자동 채운다:
 *  · 스칼라 질문 라벨 ↔ 회사/비서 필드
 *  · 반복 그룹(Shareholders/Subscribers, Controllers, Directors) → sub{id}_* / ctrl_{id}/controller_* / 서명이사
 * 못 맞춘 항목은 공란(오퍼레이터가 ✨/직접 입력). readable 이 아니라 schema+raw 로 결정적으로 매핑.
 */
import { nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import type { CustomFormQuestion } from '@/features/setupEntity/api/customFormApi';

function str(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // 배열은 **원시값(문자/숫자)만** 이어붙인다 — 객체(주소 파트 등)는 "[object Object]" 가 되므로 제외.
  if (Array.isArray(v)) {
    return v
      .filter((x) => typeof x === 'string' || typeof x === 'number')
      .map((x) => String(x).trim())
      .filter(Boolean)
      .join(', ');
  }
  return ''; // object → 텍스트 아님, 채우지 않음
}
/** 생년월일 → ISO(YYYY-MM-DD). 폼 날짜칸은 표시 형식(DD/MM/YYYY 등)으로 올 수 있다. */
function isoDate(v: unknown): string {
  const s = str(v);
  if (!s) return '';
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/); // DD/MM/YYYY (SG 폼 기본)
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return s;
}
function b04(v: unknown): string {
  const s = str(v);
  if (!s) return '';
  return nationalityLabelForOutput(s) || s;
}
/** 라벨 정규화 — 소문자·영숫자만, 앞 질문번호("12.") 제거. */
function norm(s: unknown): string {
  return String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/^\d+\s+/, '');
}
/** 괄호 안 보조 설명을 뺀 본문 라벨 */
function mainLabel(label: unknown): string {
  return String(label ?? '').replace(/\([^)]*\)/g, ' ');
}
/** label 이 synonyms 중 하나와 얼마나 맞는지 점수(0=불일치, 클수록 강함).
 *  괄호 안 보조 설명으로만 맞으면 감점 — "Full name (as shown on your Passport/ID)" 가
 *  "Passport or NRIC Number" 와 동점이 돼 이름이 NRIC 칸에 들어가던 문제. */
function score(label: string, synonyms: string[]): number {
  const main = rawScore(mainLabel(label), synonyms);
  if (main) return main;
  return Math.max(0, rawScore(label, synonyms) - 200);
}
/** **단어 단위**로 비교한다 — 글자 부분일치면 "infl-uen-ce" 가 UEN, "fin-ancial" 이 FIN 으로 잡힌다. */
function rawScore(label: string, synonyms: string[]): number {
  const n = norm(label);
  if (!n) return 0;
  const padded = ` ${n} `;
  let best = 0;
  for (const syn of synonyms) {
    const ns = norm(syn);
    if (!ns) continue;
    if (n === ns) best = Math.max(best, 1000);
    else if (padded.includes(` ${ns} `)) best = Math.max(best, 500 + ns.length);
    // 라벨이 동의어 안에 통째로 들어가는 경우(예: "Registration" ⊂ "company registration") — 두 단어 이상만.
    //   한 단어 라벨("Address")이 "email address" 같은 다른 칸 동의어에 잡히지 않게.
    else if (` ${ns} `.includes(padded) && n.length >= 4 && n.includes(' ')) best = Math.max(best, 300 + n.length);
  }
  return best;
}
/** 라벨(괄호 포함 전체)에 words 중 하나가 단어로 들어 있나 */
function labelHasAny(label: string, words: string[]): boolean {
  const padded = ` ${norm(label)} `;
  return words.some((w) => {
    const nw = norm(w);
    return !!nw && padded.includes(` ${nw} `);
  });
}

interface Cand { label: string; value: unknown; type?: string; sourceCode?: string | null }
type Scalar = Cand;
interface GroupInst { fields: Cand[] }
interface GroupQ { label: string; instances: GroupInst[] }

/** 값 종류 — 라벨이 맞아도 값 모양이 다르면 후보에서 뺀다 */
type Kind = 'name' | 'id' | 'address' | 'nat' | 'date' | 'email' | 'phone' | 'text';
interface Spec {
  syn: string[];
  kind?: Kind;
  /** 라벨에 이 단어가 있으면 감점(-150) — 같은 단어를 공유하는 다른 질문(만료일·회사 업종 등)에 지게 */
  avoid?: string[];
  /** 알려진 고객 폼의 질문 문구(exactLabel 기준) — 정확히 일치하는 질문이 있으면 점수 계산 없이 그 질문만 쓴다 */
  exact?: string[];
}
/** 정확 매칭용 문구 — 앞 질문번호("12.") · 괄호 설명 · '*' 를 빼고 소문자·영숫자만 */
function exactLabel(label: unknown): string {
  return norm(mainLabel(String(label ?? '').replace(/\*/g, ' ')));
}
const AVOID_PENALTY = 150;
const YES_NO_RE = /^(yes|no|y|n|true|false|n\/?a|nil|none|-)$/i;
/** 사람·번호·주소 칸에는 Yes/No 답, 날짜·이메일·국적 선택 칸 값이 들어가면 안 된다 */
const PLAIN_TEXT_KINDS: Kind[] = ['name', 'id', 'address'];

function candScore(c: Cand, spec: Spec): number {
  if (c.type === 'file' || c.type === 'image') return 0; // 업로드 칸(파일명) 제외
  const val = str(c.value);
  if (!val) return 0;
  let sc = score(c.label, spec.syn);
  if (!sc) return 0;
  // 피할 단어는 **괄호 밖 본문**만 본다 — "(… ACRA transaction number)" 같은 보조 설명 때문에
  //   진짜 회사명 질문이 감점돼 "purpose of setting up your company" 같은 질문에 지던 문제.
  if (spec.avoid?.length && labelHasAny(mainLabel(c.label), spec.avoid)) sc -= AVOID_PENALTY;
  const k = spec.kind;
  if (k && PLAIN_TEXT_KINDS.includes(k)) {
    if (c.type === 'email' || c.type === 'date') return 0;
    if (c.type === 'number' && k !== 'id') return 0;
    if (c.sourceCode === 'B04') return 0; // 국적 코드 선택칸
    if (YES_NO_RE.test(val)) return 0;
  }
  // 값 모양 확인 — 이메일은 @, 전화·날짜는 숫자가 있어야 한다
  if (k === 'email' && !val.includes('@')) return 0;
  if ((k === 'phone' || k === 'date') && !/\d/.test(val)) return 0;
  if (k === 'email' && c.type === 'email') sc += 100;
  if (k === 'date' && c.type === 'date') sc += 100;
  if (k === 'nat' && c.sourceCode === 'B04') sc += 100;
  return sc;
}
/** 후보 중 최고점(≥300) 값. 동점이면 본문 라벨이 더 짧은(구체적인) 질문.
 *  notEqual: 같은 행의 다른 칸 값(예: 이름)과 똑같으면 잘못 잡힌 것 → 제외. */
function pickBest(cands: Cand[], spec: Spec, notEqual: string[] = []): string {
  // 1) 알려진 폼 — 문구가 정확히 같은 질문이 있으면 그 답만 쓴다(비어 있으면 빈칸, 추정으로 넘어가지 않음)
  if (spec.exact?.length) {
    const wanted = spec.exact.map((x) => norm(x));
    const hits = cands.filter((c) => c.type !== 'file' && c.type !== 'image' && wanted.includes(exactLabel(c.label)));
    if (hits.length) {
      // 문구 우선순위(spec.exact 순서)대로 값 있는 첫 질문
      for (const w of wanted) {
        // 질문은 정확히 맞아도 값 모양은 확인 — 이메일 칸에 '@' 없는 값, 전화·날짜 칸에 숫자 없는 값은 넣지 않는다
        const h = hits.find((c) => {
          const v = str(c.value);
          if (exactLabel(c.label) !== w || !v) return false;
          if (spec.kind === 'email' && !v.includes('@')) return false;
          if ((spec.kind === 'phone' || spec.kind === 'date') && !/\d/.test(v)) return false;
          return true;
        });
        if (h) return str(h.value);
      }
      return '';
    }
  }
  // 2) 처음 보는 폼 — 라벨 유사도로 추정
  const banned = notEqual.map((x) => x.trim().toLowerCase()).filter(Boolean);
  let bestVal = '';
  let bestScore = 299;
  let bestWords = Infinity;
  for (const c of cands) {
    const sc = candScore(c, spec);
    if (sc < 300) continue;
    const val = str(c.value);
    if (banned.includes(val.toLowerCase())) continue;
    const words = norm(mainLabel(c.label)).split(' ').length;
    if (sc > bestScore || (sc === bestScore && words < bestWords)) {
      bestScore = sc;
      bestVal = val;
      bestWords = words;
    }
  }
  return bestVal;
}

export interface SingbacPreCustomFormMapped {
  values: Record<string, string>;
  local: {
    controllerIds: number[];
    secretaryIds: number[];
    directorIds: number[];
    subscriberIds: number[];
  };
}

/** questions + raw({[qid]:value|obj|obj[]}) → 스칼라/그룹 평탄화 */
function collect(questions: CustomFormQuestion[], raw: Record<string, any>) {
  const scalars: Scalar[] = [];
  const groups: GroupQ[] = [];
  for (const q of questions || []) {
    if (!q || q.type === 'image') continue;
    const rv = raw?.[q.id];
    if (q.type === 'group') {
      const rows: any[] = Array.isArray(rv) ? rv : rv && typeof rv === 'object' ? [rv] : [];
      const instances: GroupInst[] = rows.map((row) => ({
        fields: (q.fields || []).map((f) => ({ label: f.label, value: row?.[f.id], type: f.type, sourceCode: f.sourceCode })),
      }));
      groups.push({ label: q.label || '', instances });
    } else {
      scalars.push({ label: q.label || '', value: rv, type: q.type, sourceCode: q.sourceCode });
    }
  }
  return { scalars, groups };
}

/** scalars 중 spec 에 가장 잘 맞는 값 (값 있는 질문만 — 업로드·빈칸 제외). */
function pickScalar(scalars: Scalar[], spec: Spec): string {
  return pickBest(scalars, spec);
}
/** 그룹 인스턴스의 child field 중 spec 매칭 값. */
function pickField(inst: GroupInst, spec: Spec, notEqual: string[] = []): string {
  return pickBest(inst.fields, spec, notEqual);
}

// 그룹 라벨 판정용 (단어 단위라 복수형도 따로 둔다)
const SYN = {
  gSubscribers: ['shareholder', 'subscriber', 'member', 'shareholders', 'subscribers', 'members'],
  gControllers: ['controller', 'registrable controller', 'ubo', 'beneficial owner', 'controllers', 'beneficial owners', 'ubos'],
  gDirectors: ['director', 'directors', 'board of director', 'board of directors'],
};
/** 문서 칸별 매칭 규칙 — exact(알려진 고객 폼 문구) · syn(라벨 동의어) · kind(값 모양) · avoid(같은 단어를 쓰는 다른 질문)
 *  exact = Singbac 설립 양식(1~26번)·인수 양식(번호 있음/없음) 4종의 질문 문구. 앞 번호·'*'·괄호 설명은 빼고 비교. */
const SPEC = {
  companyName: {
    exact: ['please indicate your first choice of your desired company name for us to check on the availability', 'name of company'],
    syn: ['company name', 'proposed company name', 'name of company', 'name of the company', 'company', 'entity name'],
    kind: 'name',
    avoid: ['activity', 'activities', 'secretary', 'email', 'phone', 'address', 'registration', 'uen', 'number', 'type', 'capital', 'constitution', 'alternative', 'second', 'former', 'previous', 'director', 'directors', 'shareholder', 'shareholders',
      // "company" 가 들어간 서술형 질문(설립 목적·업종·웹사이트·자산 등)
      'purpose', 'nature', 'business', 'website', 'setting', 'holding', 'assets', 'funds', 'officer', 'transacting'],
  },
  regNo: { syn: ['company registration', 'registration no', 'registration number', 'uen', 'company no'], kind: 'id' },
  office: {
    syn: ['registered office', 'registered address', 'office address', 'registered office address'],
    kind: 'address',
    avoid: ['email', 'previous', 'former', 'proof', 'upload'],
  },
  shareCapital: { syn: ['share capital', 'paid up capital', 'issued capital', 'capital'], avoid: ['currency', 'class', 'type'] },
  secretaryName: {
    syn: ['secretary name', 'company secretary', 'name of secretary', 'secretary'],
    kind: 'name',
    avoid: ['nric', 'fin', 'passport', 'id', 'identification', 'address', 'nationality', 'email', 'phone', 'mobile', 'contact', 'require', 'need', 'appoint', 'provide'],
  },
  secretaryAddress: { syn: ['secretary residential address', 'secretary address', 'secretary residential'], kind: 'address', avoid: ['email', 'proof', 'upload'] },
  secretaryId: {
    syn: ['secretary nric', 'secretary fin', 'secretary passport', 'secretary identification', 'secretary id'],
    kind: 'id',
    avoid: ['expiry', 'expiration', 'issue', 'date', 'type', 'upload'],
  },
  secretaryNat: { syn: ['secretary nationality'], kind: 'nat' },
  // group child fields
  fName: {
    exact: ['full name'],
    syn: ['name', 'full name', 'name in full'],
    kind: 'name',
    avoid: ['company', 'entity', 'corporate', 'maiden', 'former', 'previous', 'alias', 'other', 'chinese', 'father', 'mother', 'spouse', 'nominee', 'emergency', 'contact'],
  },
  fRole: { exact: ['role'], syn: ['role', 'role director shareholder', 'director shareholder both', 'officer type', 'position'] },
  fAddress: {
    exact: ['residential/contact address'],
    syn: ['address', 'residential address', 'permanent address'],
    kind: 'address',
    avoid: ['email', 'proof', 'upload', 'web', 'website', 'ip'],
  },
  fShares: {
    syn: ['shares', 'number of shares', 'no of shares', 'shareholding', 'number of share', 'no of share'],
    avoid: ['class', 'type', 'price', 'currency', 'certificate', 'amount'],
  },
  fId: {
    exact: ['passport or nric number', 'passport or nric'],
    syn: ['nric', 'fin', 'passport', 'identification', 'id no', 'id number', 'passport no', 'passport number', 'nric no', 'nric number'],
    kind: 'id',
    avoid: ['expiry', 'expiration', 'issue', 'issued', 'date', 'type', 'country', 'place', 'upload', 'copy'],
  },
  fNat: { exact: ['nationality'], syn: ['nationality', 'nationalities', 'citizenship'], kind: 'nat', avoid: ['other', 'dual', 'second', 'previous', 'former'] },
  fDob: { exact: ['date of birth'], syn: ['date of birth', 'dob', 'birth', 'birthday'], kind: 'date', avoid: ['place', 'country', 'city', 'town'] },
  fEmail: { exact: ['email'], syn: ['email', 'e-mail'], kind: 'email' },
  fMobile: { exact: ['mobile no.'], syn: ['mobile', 'contact no', 'phone', 'contact number', 'telephone', 'tel', 'mobile no', 'mobile number', 'phone number'], kind: 'phone', avoid: ['emergency', 'fax'] },
  fUen: { syn: ['uen', 'registration no', 'entity number', 'registration number'], kind: 'id', avoid: ['vehicle'] },
} satisfies Record<string, Spec>;

/**
 * schema questions + raw answers → Singbac Pre 값(+ Notice controller 부수).
 */
export function mapCustomFormAnswerToSingbacPre(
  questions: CustomFormQuestion[] | null | undefined,
  raw: Record<string, any> | null | undefined,
): SingbacPreCustomFormMapped {
  const values: Record<string, string> = {};
  const set = (k: string, v: unknown) => {
    const s = str(v);
    if (s) values[k] = s;
  };
  const empty: SingbacPreCustomFormMapped = { values, local: { controllerIds: [1], secretaryIds: [1], directorIds: [1], subscriberIds: [1] } };
  if (!Array.isArray(questions) || !raw || typeof raw !== 'object') return empty;

  const { scalars, groups } = collect(questions, raw);

  // ── 회사 ──
  set('company_name', pickScalar(scalars, SPEC.companyName));
  set('company_registration_no', pickScalar(scalars, SPEC.regNo));
  set('registered_office_address', pickScalar(scalars, SPEC.office));
  set('share_capital_amount', pickScalar(scalars, SPEC.shareCapital));

  // ── Form 45B — Secretary (부수 sec_{id}/ · 폼의 단일 비서는 sec_1/ 로) ──
  set('sec_1/secretary_name', pickScalar(scalars, SPEC.secretaryName));
  set('sec_1/secretary_residential_address', pickScalar(scalars, SPEC.secretaryAddress));
  set('sec_1/secretary_identification_no', pickScalar(scalars, SPEC.secretaryId));
  const secNat = pickScalar(scalars, SPEC.secretaryNat);
  if (secNat) set('sec_1/secretary_nationality', b04(secNat));

  // ── 임원(이사/주주/컨트롤러) 수집 ──────────────────────────────────────────
  //  폼마다 그룹 구성이 다르다:
  //   (a) 라벨이 'Shareholders'/'Directors'/'Controllers' 인 그룹 → 그 종류로 확정,
  //   (b) 'Section 1 — No Change...' 처럼 라벨은 애매하지만 인물 필드(이름+역할/NRIC 등)를 가진 그룹 →
  //       행마다 'Role'(Director / Shareholder / Both) 필드로 분류. Role 필드가 없으면 전부(both) 취급.
  //  분류 결과: subscribers(주주=Constitution), directors(서명이사), controllers(Notice, 주주/컨트롤러).
  interface Person { name: string; role: 'director' | 'shareholder' | 'both' | 'controller' | 'secretary' }
  const persons: Person[] = [];
  const rowOf = new Map<Person, GroupInst>();

  function hasFieldMatching(g: GroupQ, spec: Spec): boolean {
    return g.instances.some((inst) => inst.fields.some((f) => f.type !== 'file' && score(f.label, spec.syn) >= 300));
  }
  function isPersonGroup(g: GroupQ): boolean {
    // 이름 필드 + (역할|NRIC|국적|생년월일) 중 하나 = 인물 그룹
    return (
      hasFieldMatching(g, SPEC.fName) &&
      (hasFieldMatching(g, SPEC.fRole) ||
        hasFieldMatching(g, SPEC.fId) ||
        hasFieldMatching(g, SPEC.fNat) ||
        hasFieldMatching(g, SPEC.fDob))
    );
  }

  for (const g of groups) {
    if (!isPersonGroup(g)) continue;
    const isShGroup = score(g.label, SYN.gSubscribers) >= 300;
    const isDirGroup = score(g.label, SYN.gDirectors) >= 300;
    const isCtrlGroup = score(g.label, SYN.gControllers) >= 300;
    const groupHasRole = hasFieldMatching(g, SPEC.fRole);

    for (const inst of g.instances) {
      const name = pickField(inst, SPEC.fName);
      if (!name) continue; // 빈 행(예: 미작성 Section 2) 스킵
      let role: Person['role'];
      if (isCtrlGroup) {
        role = 'controller';
      } else if (groupHasRole) {
        // 혼합 그룹(예: "Director & Shareholder Details")은 그룹 라벨이 director/shareholder 를
        //   동시에 포함해 라벨만으론 못 가른다 → 행별 Role 필드(Director/Shareholder/Both)를 **최우선** 판정.
        const r = pickField(inst, SPEC.fRole).toLowerCase();
        const isDir = /director|both/.test(r);
        const isSh = /shareholder|member|subscriber|both/.test(r);
        // Role 이 Secretary / Controller 인 행은 이사·주주로 넣지 않는다(예전엔 'both' 로 Form 45·Constitution 에 들어갔다)
        role = isDir && isSh ? 'both' : isDir ? 'director' : isSh ? 'shareholder'
          : /secretary/.test(r) ? 'secretary'
          : /controller|ubo|beneficial/.test(r) ? 'controller'
          : 'both';
      } else if (isShGroup && isDirGroup) {
        role = 'both'; // 라벨이 둘 다 가리키는데 Role 필드가 없음 → 최대한 채움
      } else if (isShGroup) {
        role = 'shareholder';
      } else if (isDirGroup) {
        role = 'director';
      } else {
        role = 'both'; // 역할 정보 없음 → 최대한 채움
      }
      const p: Person = { name, role };
      persons.push(p);
      rowOf.set(p, inst);
    }
  }

  const shareholders = persons.filter((p) => p.role === 'shareholder' || p.role === 'both');
  const directors = persons.filter((p) => p.role === 'director' || p.role === 'both');
  // registrable controller 는 법적 판정 사항이라 주주로 proxy 자동생성하지 않는다.
  //   폼에 **명시적 controller/UBO 그룹**이 있을 때만 그 인원을 채우고, 없으면 공란(오퍼레이터 판정/입력).
  const controllers = persons.filter((p) => p.role === 'controller');

  // 서명 이사(Notice) = 첫 director
  const firstDirName = directors[0]?.name || '';

  // ── Constitution subscribers ← shareholders ──
  const subscriberIds: number[] = [];
  shareholders.forEach((p, i) => {
    const inst = rowOf.get(p)!;
    const id = i + 1;
    subscriberIds.push(id);
    set(`sub${id}_subscriber_name`, p.name);
    set(`sub${id}_subscriber_address`, pickField(inst, SPEC.fAddress, [p.name]));
    set(`sub${id}_subscriber_shares`, pickField(inst, SPEC.fShares));
    // 같은 사람의 ID/국적도 실어둔다 — role 'both'(이사이자 주주)일 때 E-Filing 에서
    //   같은 사람(동일 ID)으로 병합되게 하기 위함(직책만 합쳐 1행).
    set(`sub${id}_subscriber_id`, pickField(inst, SPEC.fId, [p.name]));
    const subNat = pickField(inst, SPEC.fNat, [p.name]);
    if (subNat) set(`sub${id}_subscriber_nationality`, b04(subNat));
    // 문서엔 칸이 없지만 E-Filing(주주 생년월일·이메일·휴대폰)에 쓰는 값 — 저장값에 실어 둔다.
    set(`sub${id}_subscriber_dob`, isoDate(pickField(inst, SPEC.fDob, [p.name])));
    set(`sub${id}_subscriber_email`, pickField(inst, SPEC.fEmail));
    set(`sub${id}_subscriber_mobile`, pickField(inst, SPEC.fMobile));
  });

  // ── Form 45 (Consent to Act as Director) ← directors (각 1부, dir_{id}/) ──
  const directorIds: number[] = [];
  directors.forEach((p, i) => {
    const inst = rowOf.get(p)!;
    const id = i + 1;
    directorIds.push(id);
    const sc = `dir_${id}/`;
    set(sc + 'director_name', p.name);
    // 커스텀폼의 "Residential/Contact Address" 는 주소·연락처 겸용 단일 칸 →
    //   Form 45 의 Residential Address / Contact Address 두 행에 모두 자동세팅(오퍼레이터가 개별 수정 가능).
    const addr = pickField(inst, SPEC.fAddress, [p.name]);
    set(sc + 'director_residential_address', addr);
    set(sc + 'director_contact_address', addr);
    set(sc + 'director_identification_no', pickField(inst, SPEC.fId, [p.name]));
    const nat = pickField(inst, SPEC.fNat, [p.name]);
    if (nat) set(sc + 'director_nationality', b04(nat));
    // E-Filing(이사 생년월일·이메일·휴대폰)용 — Form 45 화면엔 없어도 저장값에 실어 둔다.
    set(sc + 'director_date_of_birth', isoDate(pickField(inst, SPEC.fDob, [p.name])));
    set(sc + 'director_email', pickField(inst, SPEC.fEmail));
    set(sc + 'director_mobile_no', pickField(inst, SPEC.fMobile));
  });
  if (!directorIds.length) directorIds.push(1);

  // ── Form 45B (Secretary) ← Role 이 Secretary 인 행 — 폼에 비서 단독 질문이 없을 때만 sec_1/ 로 채움 ──
  const secRow = persons.find((p) => p.role === 'secretary');
  if (secRow && !values['sec_1/secretary_name']) {
    const p = secRow;
    const inst = rowOf.get(p)!;
    set('sec_1/secretary_name', p.name);
    set('sec_1/secretary_residential_address', pickField(inst, SPEC.fAddress, [p.name]));
    set('sec_1/secretary_identification_no', pickField(inst, SPEC.fId, [p.name]));
    const nat = pickField(inst, SPEC.fNat, [p.name]);
    if (nat) set('sec_1/secretary_nationality', b04(nat));
  }
  // ── Notice to Controller ← controllers (각 1부) ──
  const controllerIds: number[] = [];
  controllers.forEach((p, i) => {
    const inst = rowOf.get(p)!;
    const id = i + 1;
    controllerIds.push(id);
    const sc = `ctrl_${id}/`;
    const uen = pickField(inst, SPEC.fUen, [p.name]);
    const isCorp = !!uen;
    set(sc + 'controller_name', p.name);
    set(sc + 'confirming_controller_name', p.name);
    if (isCorp) {
      set(sc + 'controller_entity_name', p.name);
      set(sc + 'controller_entity_uen', uen);
      set(sc + 'controller_registered_office_address', pickField(inst, SPEC.fAddress, [p.name]));
    } else {
      set(sc + 'controller_full_name', p.name);
      set(sc + 'controller_identification_no', pickField(inst, SPEC.fId, [p.name]));
      const nat = pickField(inst, SPEC.fNat, [p.name]);
      if (nat) set(sc + 'controller_nationality', b04(nat));
      set(sc + 'controller_date_of_birth', isoDate(pickField(inst, SPEC.fDob, [p.name])));
      set(sc + 'controller_residential_address', pickField(inst, SPEC.fAddress, [p.name]));
    }
    set(sc + 'controller_email', pickField(inst, SPEC.fEmail));
    set(sc + 'controller_mobile_no', pickField(inst, SPEC.fMobile));
    if (firstDirName) set(sc + 'director_name', firstDirName);
  });
  if (!controllerIds.length) {
    controllerIds.push(1);
    if (firstDirName) set('ctrl_1/director_name', firstDirName);
  }

  if (!subscriberIds.length) subscriberIds.push(1);
  return { values, local: { controllerIds, secretaryIds: [1], directorIds, subscriberIds } };
}
